import React, { useEffect, useMemo, useState } from 'react';
import { NativeCheckbox } from '@/components/ui/checkbox';
import { formatCurrency, formatDateDisplay, getExpectedCloseDateUrgency } from '@vanilla/utils/Utils.js';
import { getStageLabel } from '@vanilla/constants/Stages.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { TABLE_COLUMNS, getTableColumnPrefs, saveTableColumnPrefs } from '@vanilla/constants/DealDisplayFields.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { DealService } from '@vanilla/services/DealService.js';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { AdminDataApiService } from '@vanilla/services/crm/AdminDataApiService.js';
import { ToastManager } from '@/lib/toast.js';
import { confirmAlert } from '@/lib/confirmAlert';
import { useTableVirtualizer } from '@/lib/useTableVirtualizer.js';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

function formatCurrencyWithCompact(value) {
    const v = Number(value) || 0;
    if (v === 0) return { full: '$0', compact: '$0' };
    const full = formatCurrency(v, 0);
    const prefix = v < 0 ? '-' : '';
    const absValue = Math.abs(v);
    let compact;
    if (absValue >= 1000000) {
        compact = `${prefix}$${(absValue / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
    } else if (absValue >= 1000) {
        compact = `${prefix}$${(absValue / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    } else {
        compact = `${prefix}$${absValue.toFixed(0)}`;
    }
    return { full, compact };
}

const COLUMN_WIDTHS = {
    dealName: '220px', clientName: '200px', dealOwner: '150px', dealStage: '140px',
    dealAmount: '130px', forecastAmount: '140px', probability: '110px', expectedCloseDate: '150px',
    nextStep: '180px', status: '120px', dealPipeline: '150px', createdAt: '140px',
    createdByName: '150px', updatedAt: '170px', updatedByName: '160px',
};
const DEFAULT_COLUMN_WIDTH = '140px';

function getCellValue(deal, key) {
    if ((key === 'dealAmount' || key === 'forecastAmount') && !permissions.canViewFinancials('deals')) {
        return '—';
    }
    switch (key) {
        case 'dealStage': return getStageLabel(deal.dealStage);
        case 'dealAmount': return formatCurrencyWithCompact(deal.dealAmount || deal.amount || 0);
        case 'forecastAmount': return formatCurrencyWithCompact(deal.forecastAmount || 0);
        case 'probability': return `${deal.probability ?? 0}%`;
        case 'expectedCloseDate': return deal.expectedCloseDate ? formatDateDisplay(deal.expectedCloseDate) : '—';
        case 'dealOwner': return deal.dealOwner || deal.ownerName || '—';
        case 'createdAt': return deal.createdAt ? formatDateDisplay(deal.createdAt) : '—';
        case 'updatedAt': return deal.updatedAt ? formatDateDisplay(deal.updatedAt) : '—';
        case 'nextStep': return deal.nextStep || '—';
        case 'clientName': return deal.clientName || deal.accountName || '—';
        case 'dealPipeline': return PipelineStore.getLabel(deal.dealPipeline) || '—';
        case 'status': return deal.status || '—';
        default: return deal[key] ?? '—';
    }
}

function getSortValue(deal, key) {
    if (key === 'expectedCloseDate') return deal.expectedCloseDate || '';
    if (key === 'dealAmount' || key === 'forecastAmount' || key === 'probability') return deal[key] || 0;
    if (key === 'dealStage') return getStageLabel(deal.dealStage);
    if (key === 'clientName') return deal.clientName || deal.accountName || '';
    if (key === 'dealOwner') return deal.dealOwner || deal.ownerName || '';
    if (key === 'dealPipeline') return PipelineStore.getLabel(deal.dealPipeline) || '';
    return deal[key] || '';
}

function sortDeals(deals, sortKey, asc) {
    return [...deals].sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (typeof va === 'number' && typeof vb === 'number') {
            return asc ? va - vb : vb - va;
        }
        return asc
            ? String(va).toLowerCase().localeCompare(String(vb).toLowerCase())
            : String(vb).toLowerCase().localeCompare(String(va).toLowerCase());
    });
}

export default function TableView({ deals, onEdit, onRefresh, version }) {
    const [sortKey, setSortKey] = useState('dealName');
    const [sortAsc, setSortAsc] = useState(true);
    const [cols, setCols] = useState(() => getTableColumnPrefs());
    const [dragKey, setDragKey] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    useEffect(() => {
        setCols(getTableColumnPrefs());
    }, [version]);

    useEffect(() => {
        const onSettingsChanged = () => {
            setCols(getTableColumnPrefs());
        };
        window.addEventListener('displaySettingsChanged', onSettingsChanged);
        return () => window.removeEventListener('displaySettingsChanged', onSettingsChanged);
    }, []);

    const showBulkUi = permissions.canDataManagement('deals');
    const canSoftDelete = permissions.canDelete('deals');
    const canHardDelete = permissions.canDataManagement('deals');

    const visibleColumns = useMemo(() => {
        let columns = cols
            .map((key) => TABLE_COLUMNS.find((c) => c.key === key))
            .filter(Boolean);
        if (!permissions.canViewFinancials('deals')) {
            columns = columns.filter((c) => c.key !== 'dealAmount' && c.key !== 'forecastAmount');
        }
        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cols, version]);

    const sorted = useMemo(() => sortDeals(deals, sortKey, sortAsc), [deals, sortKey, sortAsc]);
    const { parentRef, enabled: virtualEnabled, virtualRows, totalSize } = useTableVirtualizer(sorted);
    const bodyColSpan = visibleColumns.length + (showBulkUi ? 1 : 0);
    const paddingTop = virtualEnabled && virtualRows?.length ? virtualRows[0].start : 0;
    const paddingBottom = virtualEnabled && virtualRows?.length
        ? totalSize - virtualRows[virtualRows.length - 1].end
        : 0;

    const onHeaderClick = (col) => {
        if (!col.sortable) return;
        if (sortKey === col.key) {
            setSortAsc((a) => !a);
        } else {
            setSortKey(col.key);
            setSortAsc(true);
        }
    };

    const onColDrop = (targetKey) => {
        if (!dragKey || targetKey === dragKey) return;
        const fromIdx = cols.indexOf(dragKey);
        const toIdx = cols.indexOf(targetKey);
        if (fromIdx === -1 || toIdx === -1) return;
        const next = [...cols];
        next.splice(fromIdx, 1);
        next.splice(toIdx, 0, dragKey);
        setCols(next);
        saveTableColumnPrefs(next);
        setDragKey(null);
    };

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(String(id));
            else next.delete(String(id));
            return next;
        });
    };

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? new Set(sorted.map((d) => String(d.id))) : new Set());
    };

    const clearSelection = () => setSelectedIds(new Set());

    const allChecked = sorted.length > 0 && sorted.every((d) => selectedIds.has(String(d.id)));
    const someChecked = sorted.some((d) => selectedIds.has(String(d.id)));

    const suppressOwnDeleteEvents = (ids) => {
        for (const id of ids) {
            if (id == null || id === '') continue;
            window._suppressRealtimeToastFor?.('deal.deleted', id);
            window._suppressRealtimeRefreshFor?.('deal.deleted', id);
        }
    };

    const softDeleteSelected = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        const label = ids.length === 1 ? 'deal' : 'deals';
        const ok = await confirmAlert({
            title: 'Soft delete',
            message: `Soft delete ${ids.length} ${label}? They can be restored from Data Management.`,
            confirmLabel: 'Soft delete',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        let errors = 0;
        try {
            for (const id of ids) {
                suppressOwnDeleteEvents([id]);
                try {
                    await DealService.delete(id);
                } catch (err) {
                    errors += 1;
                    console.error('Soft delete failed:', id, err);
                }
            }
            CrmApiRepository.clearCache();
            clearSelection();
            await onRefresh?.();
            if (errors) {
                ToastManager.warning('Partial delete', `${ids.length - errors} deleted; ${errors} failed.`);
            } else {
                ToastManager.success('Deleted', `${ids.length} ${label} soft-deleted. Restore from Data Management if needed.`);
            }
        } catch (err) {
            ToastManager.crmError('Delete', 'deal', err, 'Please verify permissions and try again.');
        }
    };

    const hardDeleteSelected = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        const label = ids.length === 1 ? 'deal' : 'deals';
        const ok = await confirmAlert({
            title: 'Permanent delete',
            message: `Permanently delete ${ids.length} ${label}? This cannot be undone.`,
            confirmLabel: 'Delete forever',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        suppressOwnDeleteEvents(ids);
        try {
            const res = await AdminDataApiService.purgeBulk('deals', ids);
            const data = res?.data ?? res;
            const purged = data?.purged?.length ?? ids.length;
            const errCount = data?.errors?.length ?? 0;
            CrmApiRepository.clearCache();
            clearSelection();
            await onRefresh?.();
            if (errCount) {
                ToastManager.warning('Partial purge', `${purged} permanently deleted; ${errCount} failed.`);
            } else {
                ToastManager.success('Permanently deleted', `${purged} ${label} permanently removed.`);
            }
        } catch (err) {
            ToastManager.crmError('Permanent delete', 'deal', err, 'Please verify permissions and try again.');
        }
    };

    return (
        <section id="tableView" className="view-section active">
            <div className="list-panel">
                <div className="table-header">
                    <h2>All Deals</h2>
                    <span className="list-count" id="listDealCount">{sorted.length} deal{sorted.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="crm-table-toolbar" id="dealsBulkToolbar">
                    {showBulkUi && (
                        <div className={`crm-list-bulk-bar${selectedIds.size ? ' visible' : ''}`}>
                            <span className="crm-bulk-count">{selectedIds.size} selected</span>
                            {canSoftDelete && (
                                <button type="button" className="btn btn-secondary btn-sm crm-bulk-soft-delete" onClick={() => softDeleteSelected()}>Soft delete</button>
                            )}
                            {canHardDelete && (
                                <button type="button" className="btn btn-sm link-danger crm-bulk-hard-delete" onClick={() => hardDeleteSelected()}>Delete permanently</button>
                            )}
                            <button type="button" className="btn btn-secondary btn-sm crm-bulk-clear" onClick={clearSelection}>Clear</button>
                        </div>
                    )}
                </div>
                <div className="crm-table-wrap" ref={parentRef}>
                    <Table unstyled id="forecastTable" className="forecast-table deals-list-table crm-entity-table">
                        <TableHeader unstyled id="forecastTableHead">
                            <TableRow unstyled>
                                {showBulkUi && (
                                    <TableHead unstyled className="crm-bulk-check-col" scope="col">
                                        <NativeCheckbox unstyled
                                            className="crm-bulk-select-all"
                                            aria-label="Select all"
                                            checked={allChecked}
                                            ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                                            onChange={(e) => toggleSelectAll(e.target.checked)}
                                        />
                                    </TableHead>
                                )}
                                {visibleColumns.map((col) => (
                                    <TableHead
                                        unstyled
                                        key={col.key}
                                        className={col.sortable ? 'sortable-header' : ''}
                                        draggable="true"
                                        data-column-key={col.key}
                                        style={{ width: COLUMN_WIDTHS[col.key] || DEFAULT_COLUMN_WIDTH, minWidth: COLUMN_WIDTHS[col.key] || DEFAULT_COLUMN_WIDTH }}
                                        onClick={() => onHeaderClick(col)}
                                        onDragStart={(e) => { setDragKey(col.key); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', col.key); }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDragEnter={(e) => e.preventDefault()}
                                        onDrop={(e) => { e.preventDefault(); onColDrop(col.key); }}
                                    >
                                        {col.label}
                                        {col.sortable && <span className="sort-indicator">{sortKey === col.key ? (sortAsc ? ' ▲' : ' ▼') : ' ⇅'}</span>}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody unstyled id="forecastTableBody">
                            {sorted.length === 0 ? (
                                <TableRow unstyled>
                                    <TableCell unstyled className="list-empty" colSpan={bodyColSpan}>No deals match the current filters.</TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {paddingTop > 0 && (
                                        <TableRow unstyled aria-hidden="true">
                                            <TableCell unstyled colSpan={bodyColSpan} style={{ height: paddingTop, padding: 0, border: 0 }} />
                                        </TableRow>
                                    )}
                                    {(virtualEnabled && virtualRows ? virtualRows.map((vr) => sorted[vr.index]) : sorted).map((deal) => (
                                        <TableRow
                                            unstyled
                                            key={deal.id}
                                            className="is-clickable"
                                            data-deal-id={deal.id}
                                            onClick={(e) => {
                                                if (e.target.closest('input,button,a,label')) return;
                                                onEdit(deal.id);
                                            }}
                                        >
                                            {showBulkUi && (
                                                <TableCell unstyled className="crm-bulk-check-col">
                                                    <NativeCheckbox unstyled
                                                        className="crm-row-check"
                                                        data-id={deal.id}
                                                        aria-label="Select row"
                                                        checked={selectedIds.has(String(deal.id))}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={(e) => toggleSelect(deal.id, e.target.checked)}
                                                    />
                                                </TableCell>
                                            )}
                                            {visibleColumns.map((col) => {
                                                if (col.key === 'dealName') {
                                                    return (
                                                        <TableCell unstyled className="table-cell-name-email list-deal-name" key={col.key}>
                                                            <span className="table-cell-nowrap">{deal.dealName || deal.name || '—'}</span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm ml-2"
                                                                aria-label="Copy deal link"
                                                                title="Copy link"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    import('../lib/dealLinks.js').then(({ copyDealLink }) => copyDealLink(deal.id));
                                                                }}
                                                            >
                                                                Copy link
                                                            </button>
                                                        </TableCell>
                                                    );
                                                }
                                                if (col.key === 'expectedCloseDate') {
                                                    const urgency = getExpectedCloseDateUrgency(deal.expectedCloseDate, deal.dealStage);
                                                    const text = deal.expectedCloseDate ? formatDateDisplay(deal.expectedCloseDate) : '—';
                                                    return (
                                                        <TableCell unstyled className="table-cell-nowrap" key={col.key}>
                                                            {urgency ? <span className={`close-date-${urgency}`}>{text}</span> : text}
                                                        </TableCell>
                                                    );
                                                }
                                                if (col.numeric) {
                                                    const value = getCellValue(deal, col.key);
                                                    return (
                                                        <TableCell unstyled className="table-numeric" key={col.key}>
                                                            {typeof value === 'object' && value.full && value.compact ? (
                                                                <><span className="amount-full">{value.full}</span> <span className="amount-compact">({value.compact})</span></>
                                                            ) : value}
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell unstyled className={col.key === 'nextStep' ? 'table-cell-wrap' : 'table-cell-nowrap'} key={col.key}>
                                                        {getCellValue(deal, col.key)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                    {paddingBottom > 0 && (
                                        <TableRow unstyled aria-hidden="true">
                                            <TableCell unstyled colSpan={bodyColSpan} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </section>
    );
}